import React from "react";
import PageWrapper from "../components/PageWrapper";
import "./css/Contact.css";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const About = () => {
  return (
    <>
      <Helmet>
        <title>
          About Us | GTAFanPage.com – Independent GTA 6 News & Fan Media Platform
        </title>
        <meta
          name="description"
          content="GTAFanPage.com is an independent fan-operated media platform covering GTA 6 news, Grand Theft Auto updates, guides, cheats, mods, and community content."
        />
        <link rel="canonical" href="https://www.gtafanpage.com/about-us" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About GTAFanPage.com" />
        <meta
          property="og:description"
          content="Learn about GTAFanPage.com, an independent fan-powered media site covering GTA 6, Grand Theft Auto news, guides, and community insights."
        />
        <meta property="og:url" content="https://www.gtafanpage.com/about-us" />
        <meta
          property="og:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Us | GTAFanPage.com" />
        <meta
          name="twitter:description"
          content="Independent GTA fan media platform delivering GTA 6 news, guides, cheats, mods, and community-driven content."
        />
        <meta
          name="twitter:image"
          content="https://www.gtafanpage.com/og-image.jpg"
        />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "GTAFanPage",
            url: "https://www.gtafanpage.com",
            logo: {
              "@type": "ImageObject",
              url: "https://www.gtafanpage.com/logo192.png",
            },
            description:
              "GTAFanPage.com is an independent fan-operated media platform dedicated to Grand Theft Auto news, guides, cheats, mods, and community content.",
          })}
        </script>
      </Helmet>

      <PageWrapper>
        <div className="contact-page-wrapper">
          <div className="contact-card about-card">
            {/* HERO */}
            <header className="about-hero">
              <h1>About GTAFanPage.com</h1>
              <p className="about-subtitle">
                An independent digital media and community platform covering the
                Grand Theft Auto universe.
              </p>
            </header>

            {/* INTRO */}
            <section className="about-section">
              <p>
                <strong>GTAFanPage.com</strong> is an independent digital media
                platform dedicated to comprehensive coverage of the{" "}
                <strong>Grand Theft Auto (GTA)</strong> video game franchise.
                Built for gamers, creators, and industry observers, the platform
                delivers structured, reliable, and continuously updated content
                related to GTA games, gameplay mechanics, updates, and
                community-driven creations.
              </p>

              <p>
                Covering titles from <strong>GTA III</strong> and{" "}
                <strong>Vice City</strong> to <strong>GTA V</strong>,{" "}
                <strong>GTA Online</strong>, and the upcoming{" "}
                <strong>GTA VI</strong>, GTAFanPage.com serves as an authoritative
                fan resource for one of the most influential franchises in
                gaming history.
              </p>
            </section>

            {/* MISSION */}
            <section className="about-section">
              <h3>Our Editorial Mission</h3>
              <p>
                Our editorial mission is to provide accurate, searchable, and
                high-value GTA content that meets modern digital media standards.
                We focus on clarity, relevance, and consistency to ensure readers
                find dependable information quickly and efficiently.
              </p>

              <ul className="about-list">
                <li>Publish high-quality GTA guides, walkthroughs, and tips</li>
                <li>
                  Deliver timely Grand Theft Auto news, updates, and patch
                  information
                </li>
                <li>
                  Curate cheat codes, mods, and gameplay enhancements
                </li>
                <li>
                  Support the GTA fan ecosystem through responsible reporting
                  and attribution
                </li>
              </ul>
            </section>

            {/* CONTENT COVERAGE */}
            <section className="about-section">
              <h3>Content Coverage</h3>
              <p>
                GTAFanPage.com offers in-depth coverage across multiple content
                verticals:
              </p>

              <ul className="about-list">
                <li>
                  <strong>GTA News & Updates</strong> – Official announcements,
                  updates, and clearly labeled industry reports
                </li>
                <li>
                  <strong>Guides & Walkthroughs</strong> – Mission guides,
                  completion strategies, and gameplay tutorials
                </li>
                <li>
                  <strong>Cheats & Codes</strong> – Console, PC, and in-game cheat
                  references
                </li>
                <li>
                  <strong>Mods & Custom Content</strong> – Community mods,
                  installation guides, and optimization tips
                </li>
                <li>
                  <strong>Characters, Maps & Lore</strong> – Structured insights
                  into the GTA universe
                </li>
                <li>
                  <strong>Community Contributions</strong> – Fan art, media,
                  theories, and creative showcases
                </li>
              </ul>

              <p>
                All content is organized for search visibility, user experience,
                and long-term discoverability.
              </p>
            </section>

            {/* STANDARDS */}
            <section className="about-section">
              <h3>Editorial Standards & Accuracy</h3>
              <p>
                We prioritize accuracy, transparency, and responsible content
                creation. Information published on GTAFanPage.com is reviewed for
                clarity and relevance, with speculation or unverified details
                clearly identified.
              </p>

              <ul className="about-list">
                <li>Search-engine-friendly, well-structured articles</li>
                <li>
                  Clear differentiation between confirmed information and rumors
                </li>
                <li>
                  Respect for intellectual property and fair-use guidelines
                </li>
              </ul>
            </section>

            {/* DISCLAIMER */}
            <section className="about-section disclaimer">
              <h3>Independence & Disclaimer</h3>
              <p>
                GTAFanPage.com is an independent, fan-operated media website. We
                are not affiliated with, endorsed by, or officially connected to
                Rockstar Games or its parent companies.
              </p>

              <p>
                All trademarks, game titles, and media assets referenced belong
                to their respective owners and are used solely for informational,
                editorial, and fan-community purposes.
              </p>
            </section>

            {/* FOOTER */}
            <footer className="about-footer">
              <p>
                For media inquiries, feedback, content collaboration, or
                partnership opportunities, please visit our{" "}
                <Link to="/contact" className="contact-link">
                  Contact page
                </Link>.
              </p>

              <p className="about-tagline">
                <strong>GTAFanPage.com</strong> — Independent coverage, trusted
                fan insight, and comprehensive resources for the Grand Theft
                Auto community.
              </p>
            </footer>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default About;
